import React, { useRef, useEffect, useState } from 'react';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal, Alert } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch, useFieldArray } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';
import { handleImage } from 'helper/helper';

const HOTOForm = ({ role = cookie.get('user-role') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues, watch } = useForm({
    defaultValues: {
      site_name: '',
      location_address: '',
      station_id: stationId || '',
      chargers: [{ name: '' }, { name: '' }],
      charger_configuration: '',
      internal_connection: '',
      new_evcs_connection: '',
      sanctioned_load: '',
      date_of_commissioning: '',
      google_coordinates: { latitude: '', longitude: '' },
      project_documents: '',
      project_images: '',
      project_punch_point: 'NILL',
      annexure_1: [
        { item_description: 'Documents Path', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Charger - 1 (OCPP ID)', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Charger - 2 (OCPP ID)', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'DB Panel - 1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'DB Panel - 2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Compact Sub Station KVA', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'EZVIZ CCTV Camera-1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'EZVIZ CCTV Camera-2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'CCTV Password', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'WIFI connection', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'SIM : Charger - 1', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'SIM : Charger - 2', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Canopy & Flooring', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Electrical Contractor', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
        { item_description: 'Electric Connection', serial_number: '', contact_person: '', contact_details: '', documents_path: '' },
      ],
      acknowledgment: 'Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).',
      comments: {
        project: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        maintenance: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        operations: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      other_comments: '',
      status: 'draft', // single-level workflow status
      punch_points: '',
    },
  });

  const { fields: chargerFields, append: appendCharger, remove: removeCharger } = useFieldArray({
    control,
    name: 'chargers',
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const new_evcs_connection = watch('new_evcs_connection');

  // ---------- Autosave (does NOT alter status) ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        form_data: {
          status: data.status,
          form_id: formId,
          formName: 'hoto',
          stationId: data.station_id,
          site_name: data.site_name,
          location_address: data.location_address,
          chargers: data.chargers.map((c) => c.name),
          charger_configuration: data.charger_configuration,
          internal_connection: data.internal_connection,
          new_evcs_connection: data.new_evcs_connection,
          sanctioned_load: data.new_evcs_connection === 'Yes' ? data.sanctioned_load : '',
          date_of_commissioning: data.date_of_commissioning,
          google_coordinates: data.google_coordinates,
          project_documents: data.project_documents,
          project_images: data.project_images,
          project_punch_point: data.project_punch_point,
          annexure_1: data.annexure_1,
          acknowledgment: data.acknowledgment,
          comments: data.comments,
          other_comments: data.other_comments,
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) dispatch({ type: VIEW_FORM, payload: { formId, formName: 'hoto' } });
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('site_name', d.site_name || '');
      setValue('location_address', d.location_address || '');
      setValue('station_id', d.stationId || stationId || '');
      setValue('chargers', Array.isArray(d.chargers) ? d.chargers.map((name) => ({ name })) : [{ name: '' }, { name: '' }]);
      setValue('charger_configuration', d.charger_configuration || '');
      setValue('internal_connection', d.internal_connection || '');
      setValue('new_evcs_connection', d.new_evcs_connection || '');
      setValue('sanctioned_load', d.sanctioned_load || '');
      setValue('date_of_commissioning', d.date_of_commissioning || '');
      setValue('google_coordinates.latitude', d.google_coordinates?.latitude || '');
      setValue('google_coordinates.longitude', d.google_coordinates?.longitude || '');
      setValue('project_documents', d.project_documents || '');
      setValue('project_images', d.project_images || '');
      setValue('project_punch_point', d.project_punch_point || 'NILL');
      setValue('annexure_1', Array.isArray(d.annexure_1) ? d.annexure_1 : getValues('annexure_1'));
      setValue('acknowledgment', d.acknowledgment || 'Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).');
      setValue('comments', d.comments || getValues('comments'));
      setValue('other_comments', d.other_comments || '');
      setValue('status', d.status || 'draft');
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---------- Role/stage gating ----------
  const status = useWatch({ control, name: 'status' });
  
  // Who can edit the main form fields? ONLY Engineer in draft/CONTRACTOR or Admin.
  const isMainFieldEditable = role === 'admin' || (role === 'engineer' && (status === 'draft' || status === 'CONTRACTOR'));

  // Who can sign? Role-specific gating
  const canSign = (sigRole) => {
    if (role === 'admin') return true;
    if (sigRole === 'project' && role === 'engineer' && (status === 'draft' || status === 'CONTRACTOR')) return true;
    if (sigRole === 'maintenance' && role === 'maintenance' && status === 'MANAGER_APPROVED' && (viewFormData?.maintenance_status || 'pending') !== 'approved') return true;
    if (sigRole === 'operations' && role === 'operations' && status === 'MANAGER_APPROVED' && (viewFormData?.operations_status || 'pending') !== 'approved') return true;
    // Manager signs in SUBMITTED stage? (The prompt says engineer submits, goes to manager)
    // The current signature table has project, maintenance, operations. 
    // Manager doesn't have a specific signature column in HOTO but they "Approve" the flow.
    return false;
  };

  const isEditableByManager = role === 'manager' && (status === 'SUBMITTED' || status === 'MANAGER');
  const isEditableByOpsMaint = (role === 'operations' || role === 'maintenance') && status === 'MANAGER_APPROVED';

  const isEditableStage = isMainFieldEditable || canSign('project') || canSign('maintenance') || canSign('operations') || isEditableByManager || isEditableByOpsMaint;

  // ---------- Signatures ----------
  const handleImageUpload = (e, sigRole) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => { setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true }); };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    const file = e.target.files[0];
    if (file) {
      toast.info('Uploading file...');
      const url = await handleImage(file, file.name);
      if (url) {
        setValue(fieldName, url, { shouldDirty: true, shouldValidate: true });
        toast.success('File uploaded successfully');
      } else {
        toast.error('File upload failed');
      }
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setShowModal(true);
      setCurrentSignature({ role: sigRole });
    }
  };

  const handleResetSignature = (sigRole) => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    if (!isEditableStage) { toast.info('Form is not editable at this stage'); return; }
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---------- Submit: single-level APFC flow ----------
  const onSubmit = (action) => (data) => {
    if (!isEditableStage && role !== 'admin') {
      toast.info('Form is not editable at this stage');
      return;
    }

    let nextStatus = data.status;

    if (role === 'engineer') {
      nextStatus = action === 'draft' ? 'draft' : 'SUBMITTED';
    } else if (role === 'manager') {
      nextStatus = action === 'approved' ? 'MANAGER_APPROVED' : 'draft';
    } else if (role === 'maintenance' || role === 'operations') {
      // Logic for parallel is handled in backend service, we just send 'approved'
      nextStatus = action === 'approved' ? 'approved' : 'draft';
    } else if (role === 'admin') {
      if (action === 'approved') nextStatus = 'approved';
      else if (action === 'draft') nextStatus = 'draft';
    }

    setValue('status', nextStatus);
    autoSave.cancel();

    const payload = {
      form_data: {
        status: nextStatus,
        form_id: formId,
        formName: 'hoto',
        stationId: data.station_id,
        site_name: data.site_name,
        location_address: data.location_address,
        chargers: data.chargers.map((c) => c.name),
        charger_configuration: data.charger_configuration,
        internal_connection: data.internal_connection,
        new_evcs_connection: data.new_evcs_connection,
        sanctioned_load: data.new_evcs_connection === 'Yes' ? data.sanctioned_load : '',
        date_of_commissioning: data.date_of_commissioning,
        google_coordinates: data.google_coordinates,
        project_documents: data.project_documents,
        project_images: data.project_images,
        project_punch_point: data.project_punch_point,
        annexure_1: data.annexure_1,
        acknowledgment: data.acknowledgment,
        comments: data.comments,
        other_comments: data.other_comments,
        punch_points: data.punch_points,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(`${action === 'approved' || action === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

    const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      if (pendingRework.level) {
        handleSubmit(onSubmit(pendingRework.level, pendingRework.action))();
      } else {
        handleSubmit(onSubmit(pendingRework.action))();
      }
    }
  };

if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <h1 className="text-center mb-4" style={{ fontWeight: '800', color: '#1e293b' }}>Handover Takeover Report (HOTO)</h1>

        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-4">PCS (SITE NAME) Handover Takeover Project</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      {!isEditableStage && role !== 'admin' && (
        <div className="alert alert-warning mb-4">This form is currently view-only for your role/stage.</div>
      )}

      <Form>
        <h3>1. Site Details</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Site Name</Form.Label>
              <Form.Control type="text" {...register('site_name')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Location / Address</Form.Label>
              <Form.Control type="text" {...register('location_address')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
        </Row>

        <h4>Chargers</h4>
        {chargerFields.map((field, index) => (
          <Row key={field.id} className="align-items-center mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Charger {index + 1}</Form.Label>
                <Form.Control type="text" {...register(`chargers.${index}.name`)} disabled={!isMainFieldEditable} />
              </Form.Group>
            </Col>
            {index >= 2 && isEditableStage && (
              <Col md={1}>
                <Button variant="danger" size="sm" onClick={() => removeCharger(index)} disabled={!isEditableStage}>
                  -
                </Button>
              </Col>
            )}
          </Row>
        ))}
        {isEditableStage && (
          <Button variant="primary" size="sm" onClick={() => appendCharger({ name: '' })} className="mb-3">
            + Add Charger
          </Button>
        )}

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Charger Configuration</Form.Label>
              <Form.Control type="text" {...register('charger_configuration')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
        </Row>

        <h4>Power Connection</h4>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Internal Connection</Form.Label>
              <Form.Control type="text" {...register('internal_connection')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>New EVCS Connection</Form.Label>
              <Form.Select {...register('new_evcs_connection')} disabled={!isMainFieldEditable}>
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
            </Form.Group>
          </Col>
          {new_evcs_connection === 'Yes' && (
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sanctioned Load</Form.Label>
                <Form.Control type="text" {...register('sanctioned_load')} disabled={!isEditableStage} />
              </Form.Group>
            </Col>
          )}
          <Col md={12}>
            <Form.Text className="text-muted">Note: If new EVCS connection, mention sanctioned load.</Form.Text>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Commissioning</Form.Label>
              <Form.Control type="date" {...register('date_of_commissioning')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
        </Row>

        <h4>Google Coordinates</h4>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Latitude</Form.Label>
              <Form.Control type="text" {...register('google_coordinates.latitude')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Longitude</Form.Label>
              <Form.Control type="text" {...register('google_coordinates.longitude')} disabled={!isMainFieldEditable} />
            </Form.Group>
          </Col>
        </Row>

        <h3>2. Project Attachments</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Documents (Annexure I)</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => handleFileUpload(e, 'project_documents')}
                disabled={!isMainFieldEditable}
                className="mb-2"
              />
              {formValues.project_documents && (
                <div className="mb-2">
                  <a href={formValues.project_documents} target="_blank" rel="noopener noreferrer">View Uploaded Document</a>
                </div>
              )}
              <Form.Text className="text-muted">Attached Annexure – I</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Images (Annexure II)</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => handleFileUpload(e, 'project_images')}
                disabled={!isMainFieldEditable}
                className="mb-2"
                accept="image/*"
              />
              {formValues.project_images && (
                <div className="mb-2">
                  <a href={formValues.project_images} target="_blank" rel="noopener noreferrer">View Uploaded Image</a>
                </div>
              )}
              <Form.Text className="text-muted">Attached Annexure – II</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Punch Point (Annexure III)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                {...register('project_punch_point')}
                disabled={!isMainFieldEditable}
                placeholder="Enter project punch points or pending issues"
              />
              <Form.Text className="text-muted">Attached Annexure – III</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <h3>3. Annexure I - Item Details</h3>
        <Table bordered>
          <thead>
            <tr>
              <th>Item Description</th>
              <th>Serial Number / Login ID &amp; Password / Path</th>
              <th>Contact Person/Vendor Name</th>
              <th>Contact Details</th>
              <th>Documents Path</th>
            </tr>
          </thead>
          <tbody>
            {formValues.annexure_1.map((item, index) => (
              <tr key={index}>
                <td>{item.item_description}</td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.serial_number`)} disabled={!isMainFieldEditable} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.contact_person`)} disabled={!isMainFieldEditable} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.contact_details`)} disabled={!isMainFieldEditable} /></td>
                <td><Form.Control type="text" {...register(`annexure_1.${index}.documents_path`)} disabled={!isMainFieldEditable} /></td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h3>4. Acknowledgment</h3>
        <Form.Label>Acknowledge and accept the handover of Electric Vehicle Charging Station (EVCS).</Form.Label>

        <h3>5. Signatures</h3>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Issued By Project</th>
              <th>
                Acknowledged by Maintenance (M)
                {formValues.status === 'MANAGER_APPROVED' && (
                  <div className={`badge bg-${(viewFormData?.maintenance_status || 'pending') === 'approved' ? 'success' : 'warning'}-lt ms-2`} style={{fontSize: '10px'}}>
                    {(viewFormData?.maintenance_status || 'pending').toUpperCase()}
                  </div>
                )}
              </th>
              <th>
                Acknowledged by Operations (O)
                {formValues.status === 'MANAGER_APPROVED' && (
                  <div className={`badge bg-${(viewFormData?.operations_status || 'pending') === 'approved' ? 'success' : 'warning'}-lt ms-2`} style={{fontSize: '10px'}}>
                    {(viewFormData?.operations_status || 'pending').toUpperCase()}
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map((field) => (
              <tr key={field}>
                <td>{field}</td>
                {['project', 'maintenance', 'operations'].map((sigRole) => (
                  <td key={sigRole}>
                    {field === 'Date' ? (
                      <Form.Control type="date" {...register(`comments.${sigRole}.${field.toLowerCase()}`)} disabled={!canSign(sigRole)} />
                    ) : field === 'Signature' ? (
                      <div>
                        <Form.Select
                          {...register(`comments.${sigRole}.signatureType`)}
                          onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)}
                          className="mb-2"
                          disabled={!canSign(sigRole)}
                        >
                          <option value="upload">Image Upload</option>
                          <option value="digital">Digital Sign</option>
                        </Form.Select>
                        {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, sigRole)}
                            className="mb-2"
                            disabled={!canSign(sigRole)}
                          />
                        )}
                        {_.get(formValues, `comments.${sigRole}.signature`) && (
                          <div>
                            <img
                              src={_.get(formValues, `comments.${sigRole}.signature`)}
                              alt="Signature"
                              style={{ maxWidth: '100px', marginTop: '5px' }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleResetSignature(sigRole)}
                              className="mt-2"
                              disabled={!canSign(sigRole)}
                            >
                              Reset Signature
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Form.Control type="text" {...register(`comments.${sigRole}.${field.toLowerCase()}`)} disabled={!canSign(sigRole)} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        <h3>6. Other Comments/Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={4}
            {...register('other_comments')}
            disabled={!isEditableStage}
            placeholder="Enter any additional comments or remarks"
          />
        </Form.Group>

        {/* Stage-specific actions */}
        {role === 'manager' && (status === 'SUBMITTED' || status === 'MANAGER') ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
              Approve (Send to Ops/Maint)
            </Button>
            <Button variant="danger" onClick={handleReworkClick('draft')}>
              Re-Work
            </Button>
          </div>
        ) : (role === 'maintenance' || role === 'operations') && status === 'MANAGER_APPROVED' ? (
          ((role === 'maintenance' && (viewFormData?.maintenance_status || 'pending') !== 'approved') || 
           (role === 'operations' && (viewFormData?.operations_status || 'pending') !== 'approved')) ? (
            <div className="mt-4">
              <Button variant="success" onClick={handleSubmit(onSubmit('approved'))} className="me-2">
                Approve & Sign
              </Button>
              <Button variant="danger" onClick={handleReworkClick('draft')}>
                Request Re-Work
              </Button>
            </div>
          ) : (
            <div className="alert alert-success mt-4">You have already approved this form. Pending other role approval.</div>
          )
        ) : (
          <div className="mt-4">
            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('pending'))}
              disabled={!isMainFieldEditable}
            >
              {status === 'draft' ? 'Submit' : 'Update'}
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleReworkClick('draft')}
              disabled={!isMainFieldEditable}
            >
              Save As Draft
            </Button>
          </div>
        )}

        <Modal show={showModal && isEditableStage} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditableStage}>
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditableStage}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    
      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
</div>
  );
};

HOTOForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin', 'operations', 'maintenance']),
  userId: PropTypes.string,
};

export default HOTOForm;
